import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { HULLS } from "@frontier/content/definitions";
import {
  equipmentDefinition,
  type EquipmentKey,
} from "@frontier/content/arsenal";
import type { EquipmentTier } from "@frontier/content/equipment-tiers";
import type { Profile } from "@frontier/protocol/contracts";
import type { UpgradePricing } from "./use-upgrade-prices";
import { EquipmentInventory } from "./EquipmentInventory";
import { EquipmentItemDetail } from "./EquipmentItemDetail";
import { EquipmentSummary } from "./EquipmentSummary";
import { equipmentContextLabel, equipmentCount, mountedCount } from "./equipment-selection";
import { fittingCells } from "@frontier/content/ship-fitting";
import type { EquipmentContext } from "./equipment-selection";
import {
  installedEquipmentFocus,
  sameEquipmentFocus,
  type EquipmentFocus,
} from "./equipment-focus";
import { credits } from "./ui-labels";
import { revealControl } from "./reveal-control";
import "./equipment.css";
import "./equipment-detail.css";
import "./equipment-responsive.css";

interface Props {
  context: EquipmentContext | null;
  initialFocus?: EquipmentFocus | null;
  profile: Profile;
  busy: boolean;
  progressionAllowed: boolean;
  upgradePricing: UpgradePricing;
  onContext: (context: EquipmentContext) => void;
  onClose: () => void;
  onSelect: (key: EquipmentKey | null, tier?: EquipmentTier) => Promise<Profile | void>;
  onMarket?: () => void;
  onDismantle: (key: EquipmentKey, tier: EquipmentTier) => Promise<void>;
  onUpgrade: (key: EquipmentKey, fromTier: EquipmentTier) => Promise<Profile | void>;
  onTrainingAction?: (action: "inspect" | "cell" | "install") => void;
}
interface Confirmation {
  operation: "install" | "remove" | "upgrade";
  owner: string;
  hull: Profile["hullId"];
  run: string;
  context: EquipmentContext | null;
  item: EquipmentFocus | null;
  mountedBefore: EquipmentFocus | null;
  sourceCount: number;
  nextCount: number;
}
function trainingCell(profile: Profile): EquipmentContext | undefined {
  if (!profile.shipFitting) return { kind: "module", slot: 1 };
  const fitting = profile.shipFitting;
  const cells =
    fitting.format === 2
      ? fitting.cells
      : [
          ...fitting.weapons.map((item) =>
            item ? { ...item, kind: "weapon" as const } : null,
          ),
          ...fitting.utilities,
        ];
  const empty = cells.findIndex((item) => item === null);
  const repair = cells.findIndex(
    (item) => item?.kind === "module" && item.id === "repair",
  );
  const other = cells.findIndex((item) => item?.kind !== "weapon");
  const index = empty >= 0 ? empty : repair >= 0 ? repair : other;
  return index >= 0 ? { kind: "cell", slot: index } : undefined;
}
export function EquipmentCatalog(props: Props) {
  const { context, profile, busy } = props;
  const panel = useRef<HTMLElement>(null);
  const [inspection, setInspection] = useState(0);
  const [focus, setFocus] = useState<EquipmentFocus | null>(
    () =>
      props.initialFocus ??
      (context ? installedEquipmentFocus(profile, context) : null),
  );
  const active = useRef(profile);
  active.current = profile;
  const actionSequence = useRef(0);
  const [feedback, setFeedback] = useState("");
  const [confirmedContext, setConfirmedContext] = useState<EquipmentContext | null>(null);
  const installed = context ? installedEquipmentFocus(profile, context) : null;
  const firstOwned = profile.arsenal.stacks.find((stack) => stack.count > 0);
  const training =
    profile.tutorial?.version === 2 &&
    profile.tutorial.rewardGranted &&
    !profile.tutorial.milestones.includes("moduleEquipped");
  const selected = training && !focus ? null : focus ?? installed ??
    (firstOwned ? { key: firstOwned.key, tier: firstOwned.tier } : null);
  const trainingAction =
    focus?.key.kind === "module" && focus.key.id === "volley"
      ? context
        ? "install"
        : "cell"
      : "inspect";
  useEffect(() => {
    if (training) props.onTrainingAction?.(trainingAction);
  }, [training, trainingAction, props.onTrainingAction]);
  useLayoutEffect(() => {
    const root = panel.current;
    if (!root || !training) return;
    const reveal = () => {
      const target = root.querySelector<HTMLElement>('[data-tutorial-highlight="true"]');
      if (target) revealControl(target, root.closest<HTMLElement>(".game-main, .game-shell") ?? root);
    };
    reveal();
    const frame = requestAnimationFrame(reveal);
    return () => cancelAnimationFrame(frame);
  }, [training, trainingAction, profile.playerId, profile.runId]);
  useLayoutEffect(() => {
    const root = panel.current;
    if (!root || training || inspection === 0) return;
    const boundary = root.closest<HTMLElement>(".game-main, .game-shell") ?? root;
    const target = root.querySelector<HTMLElement>(".fitting-item-hero");
    if (target && boundary.clientWidth <= 650) revealControl(target, boundary, "start");
  }, [inspection, training]);
  useEffect(() => {
    actionSequence.current++;
    setFeedback("");
    setConfirmedContext(null);
  }, [profile.playerId, profile.hullId, profile.runId]);
  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => setFeedback(""), 2400);
    return () => window.clearTimeout(timer);
  }, [feedback]);
  useEffect(() => {
    if (!confirmedContext) return;
    const timer = window.setTimeout(() => setConfirmedContext(null), 1500);
    return () => window.clearTimeout(timer);
  }, [confirmedContext]);
  const performConfirmed = async (
    operation: Confirmation["operation"], item: EquipmentFocus | null,
    perform: () => Promise<Profile | void>,
  ) => {
    const intent: Confirmation = {
      operation, owner: profile.playerId, hull: profile.hullId, run: profile.runId,
      context, item, mountedBefore: installed,
      sourceCount: item ? equipmentCount(profile.arsenal, item.key, item.tier) : 0,
      nextCount: item ? equipmentCount(profile.arsenal, item.key, item.tier === 0 ? 1 : 2) : 0,
    };
    const sequence = ++actionSequence.current;
    setFeedback("");
    setConfirmedContext(null);
    let confirmedProfile: Profile | void;
    try { confirmedProfile = await perform(); }
    catch (error) {
      if (sequence === actionSequence.current) setFeedback(error instanceof Error ? error.message : "Операция не подтверждена");
      return;
    }
    if (!confirmedProfile || sequence !== actionSequence.current ||
      [active.current, confirmedProfile].some(value => value.playerId !== intent.owner || value.hullId !== intent.hull || value.runId !== intent.run)) return;
    const mounted = intent.context ? installedEquipmentFocus(confirmedProfile, intent.context) : null;
    const nextTier: EquipmentTier = intent.item?.tier === 0 ? 1 : 2;
    const upgraded = intent.operation === "upgrade" && intent.item &&
      ((sameEquipmentFocus(intent.mountedBefore, intent.item) && intent.context !== null &&
        sameEquipmentFocus(mounted, { key: intent.item.key, tier: nextTier })) ||
       (equipmentCount(confirmedProfile.arsenal, intent.item.key, intent.item.tier) < intent.sourceCount &&
        equipmentCount(confirmedProfile.arsenal, intent.item.key, nextTier) > intent.nextCount));
    const confirmed = upgraded ||
      (intent.operation === "install" && sameEquipmentFocus(mounted, intent.item)) ||
      (intent.operation === "remove" && mounted === null);
    if (!confirmed) return;
    if (intent.context && (intent.operation !== "upgrade" || sameEquipmentFocus(intent.mountedBefore, intent.item)))
      setConfirmedContext(intent.context);
    if (upgraded && intent.item) {
      const nextFocus = { key: intent.item.key, tier: nextTier };
      setFocus(current => sameEquipmentFocus(current, intent.item) ? nextFocus : current);
    }
    setFeedback(operation === "upgrade" ? "Улучшение подтверждено" : operation === "remove" ? "Предмет снят" : "Установка подтверждена");
  };
  return (
    <section ref={panel} className="equipment-fitting" aria-label="Подбор оснащения">
      <header className="fitting-heading">
        <div>
          <span>ОСНАЩЕНИЕ / {HULLS[profile.hullId].name}</span>
          <b>ИНВЕНТАРЬ</b>
        </div>
        <small className="fitting-balance">
          {credits(profile.credits)} КР · {profile.components} КОМП.
        </small>
        <button className="fitting-close" onClick={props.onClose}>
          К КОРАБЛЮ <span aria-hidden="true">×</span>
        </button>
      </header>
      <div className="fitting-cells-heading">
        <span>
          {profile.shipFitting
            ? `НА КОРАБЛЕ · ${fittingCells(profile.shipFitting).filter(Boolean).length}/${fittingCells(profile.shipFitting).length} ЯЧЕЕК`
            : "НА КОРАБЛЕ"}
          {context && ` · Выбрана: ${equipmentContextLabel(context)}`}
        </span>
        {installed && (profile.shipFitting || context?.kind === "passive") && (
          <button
            className="fitting-remove"
            data-equipment-action="remove"
            disabled={busy}
            onClick={() => {
              void performConfirmed("remove", null, () => props.onSelect(null));
            }}
          >
            СНЯТЬ: {equipmentDefinition(installed.key).name} ·{" "}
            {context && equipmentContextLabel(context)}
          </button>
        )}
      </div>
      <EquipmentSummary
        profile={profile}
        {...(context ? { context } : {})}
        busy={busy}
        onOpen={props.onContext}
        availableItem={focus && equipmentCount(profile.arsenal, focus.key, focus.tier) > mountedCount(profile, focus.key, focus.tier) ? focus : null}
        confirmedContext={confirmedContext}
        trainingTarget={
          training && trainingAction === "cell"
            ? trainingCell(profile)
            : undefined
        }
      />
      <div className="fitting-workspace">
        <section
          className="fitting-inventory"
          aria-label="Всё принадлежащее оснащение"
        >
          <div className="fitting-stock-heading">
            <h3>НА СКЛАДЕ</h3>
            {props.progressionAllowed && props.onMarket && (
              <button type="button" onClick={props.onMarket}>
                МАГАЗИН →
              </button>
            )}
          </div>
          <div className="fitting-stock-scroll">
            <EquipmentInventory
              context={{ kind: "cell", slot: 0 }}
              profile={profile}
              selected={selected}
              showBlueprints={false}
              onInspect={item => { setFocus(item); setInspection(value => value + 1); }}
              trainingInspect={Boolean(
                training && trainingAction === "inspect",
              )}
            />
          </div>
          {!props.progressionAllowed && (
            <p className="fitting-stock-note">
              Магазин откроется после выпуска на базу.
            </p>
          )}
        </section>
        <div className="fitting-inspector">
          {selected ? (
            <EquipmentItemDetail
              {...props}
              item={equipmentDefinition(selected.key)}
              tier={selected.tier}
              trainingInstall={Boolean(
                training && trainingAction === "install",
              )}
              onSelect={(key, tier = 0) => performConfirmed(key ? "install" : "remove",
                key ? { key, tier } : null, () => props.onSelect(key, tier))}
              onUpgrade={(key, tier) => performConfirmed("upgrade", { key, tier }, () => props.onUpgrade(key, tier))}
            />
          ) : (
            <p className="fitting-empty">
              {training ? "Выбери «Усиленный залп» в инвентаре — это награда за учебный контракт." : "Склад пуст. Новые копии доступны в магазине оснащения."}
            </p>
          )}
        </div>
      </div>
      {feedback && (
        <p className="fitting-feedback" role="status">
          {feedback}
        </p>
      )}
    </section>
  );
}
